import { useState } from 'react';
import { Settings, ChevronRight } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import useJeMobil from '../../hooks/useJeMobil';

/**
 * ⚙ Nastavení modulu — NORMA (§1h, revize Nastavení 2026-10-12).
 *
 * Nastavení bydlí u práce, kterou ovlivňuje: co dřív leželo ve společném
 * Nastavení pod profilem, otevírá modul sám ozubeným kolem v hlavičce.
 * Nastavení = modal (§1f), proto `ModalShell`: na desktopu na střed se
 * seznamem sekcí vlevo, na mobilu zdola — nejdřív sekce, pak obsah se šipkou zpět.
 *
 * Kolo vidí jen ten, kdo smí nastavení měnit (`upravuje` na podstránce, které
 * se týká) — stránka ho bez oprávnění vůbec nevykreslí. Sekce bez obsahu
 * pro daného člověka se do pole nepředávají.
 *
 * ```jsx
 * {muze(user, 'smeny.plan', 'upravuje') && <NastaveniTlacitko onClick={() => setNastaveni(true)} barva={SECTION_COLOR} isDark={isDark} />}
 * {nastaveni && <NastaveniModulu nadpis="Nastavení směn" barva={SECTION_COLOR} isDark={isDark} onClose={…}
 *     sekce={[{ id: 'role', nazev: 'Role', popis: 'barva a ikona v plánu', obsah: () => <RoleSprava … /> }]} />}
 * ```
 */
export function NastaveniTlacitko({ onClick, isDark, barva, popis = 'Nastavení modulu' }) {
    return (
        <button type="button" onClick={onClick} title={popis} aria-label={popis}
            className="flex items-center justify-center shrink-0 rounded-lg"
            style={{
                width: 40, height: 40, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = barva; e.currentTarget.style.borderColor = barva; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'; }}>
            <Settings size={18} />
        </button>
    );
}

export default function NastaveniModulu({ nadpis, sekce, onClose, isDark, barva }) {
    const isMobile = useJeMobil();
    const [aktivni, setAktivni] = useState(isMobile || sekce.length === 0 ? null : sekce[0].id);
    const akt = sekce.find((s) => s.id === aktivni) || null;
    const cara = isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)';
    const tlumene = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    const seznam = (
        <nav style={isMobile ? {} : { width: 210, flexShrink: 0, borderRight: `1px solid ${cara}`, overflowY: 'auto', padding: '8px 0' }}>
            {sekce.map((s) => {
                const sel = s.id === aktivni;
                return (
                    <button key={s.id} type="button" onClick={() => setAktivni(s.id)} className="w-full text-left flex items-center"
                        style={{
                            gap: 10, padding: isMobile ? '12px 16px' : '9px 14px', minHeight: isMobile ? 56 : 44, cursor: 'pointer', border: 'none',
                            borderBottom: isMobile ? `1px solid ${cara}` : 'none',
                            background: sel ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)') : 'transparent',
                            boxShadow: sel ? `inset 3px 0 0 ${barva}` : 'none',
                        }}>
                        <span className="flex-1 min-w-0">
                            <span className="block truncate" style={{ fontSize: isMobile ? 15 : 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{s.nazev}</span>
                            {s.popis && <span className="block truncate" style={{ fontSize: 11.5, color: tlumene, marginTop: 1 }}>{s.popis}</span>}
                        </span>
                        {isMobile && <ChevronRight size={16} style={{ color: tlumene }} />}
                    </button>
                );
            })}
        </nav>
    );

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={isMobile && akt ? akt.nazev : nadpis} ikona={Settings} barva={barva} sirka={isMobile ? 460 : Math.min(1040, window.innerWidth - 48)}>
            {isMobile ? (
                akt ? (
                    <div style={{ padding: '4px 16px 16px' }}>
                        <ZpetTlacitko onClick={() => setAktivni(null)} popis="Zpět na sekce" isDark={isDark} style={{ marginBottom: 6 }} />
                        {akt.obsah()}
                    </div>
                ) : seznam
            ) : (
                <div className="flex" style={{ height: 'min(72dvh, 760px)', minHeight: 0 }}>
                    {sekce.length > 1 && seznam}
                    <div className="flex-1" style={{ overflowY: 'auto', minWidth: 0, padding: '16px 20px' }}>
                        {akt ? akt.obsah() : null}
                    </div>
                </div>
            )}
        </ModalShell>
    );
}
